import React from 'react';
import { Shield, AlertCircle, Mail } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const Privacy = () => {
  // Format current date as "Month Day, Year"
  const getCurrentDate = () => {
    const date = new Date();
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-purple-100 flex flex-col">
      <Header />
      <main className="flex-1">
        <section className="container mx-auto px-4 py-12 lg:py-16">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-purple-700 font-semibold shadow mb-6">
                <Shield className="h-4 w-4" />
                Privacy Policy
              </div>
              <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                Privacy Policy
              </h1>
              <p className="text-lg text-gray-600">
                Effective Date: February 10, 2025
                {/* Effective Date: {getCurrentDate()} */}
              </p>
            </div>

            {/* Main Content */}
            <div className="bg-white rounded-3xl shadow-xl p-8 lg:p-12 space-y-8">
              {/* Introduction */}
              <div className="space-y-4">
                <p className="text-gray-700 leading-relaxed">
                  At mySTORYme ("we", "our", or "us"), we value your privacy and are committed to protecting the personal information you share with us. This Privacy Policy explains how we collect, use, disclose, and safeguard your personal information when you visit our website (the "Site") and use our web application (the "Service"). By accessing or using our Service, you agree to the collection and use of information in accordance with this Privacy Policy.
                </p>
                
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h3 className="font-bold text-yellow-900 mb-2">Important Notice for Parents and Guardians:</h3>
                      <p className="text-yellow-800 text-sm leading-relaxed">
                        Our Service includes the creation of story profiles for children under the age of 13. In order to create these profiles, you (as the parent or legal guardian) will be required to provide certain information about your child. By providing this information, you represent and warrant that you are the child's parent or legal guardian and that you consent to the collection and use of your child's information as described in this Privacy Policy.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 1 */}
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-gray-900">1. Information We Collect</h2>
                <p className="text-gray-700 leading-relaxed">
                  We collect various types of information in connection with the operation of our Service. This includes information provided directly by you as well as information collected automatically.
                </p>
                
                <div className="space-y-4">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">a. Personal Information Provided by You</h3>
                    
                    <div className="ml-4 space-y-3">
                      <div>
                        <h4 className="font-semibold text-gray-800 mb-2">For Adult Users:</h4>
                        <p className="text-gray-700 leading-relaxed mb-2">
                          When you sign up for an account or interact with our Service as an adult, we may collect personally identifiable information ("Personal Information") such as:
                        </p>
                        <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                          <li><strong>Contact Details:</strong> Name, email address, and other similar contact data.</li>
                          <li><strong>Profile Information:</strong> Usernames, profile pictures, and any additional details you choose to share.</li>
                        </ul>
                      </div>
                      
                      <div>
                        <h4 className="font-semibold text-gray-800 mb-2">For Children's Story Profiles:</h4>
                        <p className="text-gray-700 leading-relaxed mb-2">
                          When creating a story profile for a child under the age of 13, you (the parent or legal guardian) will provide information about the child. This information may include, but is not limited to:
                        </p>
                        <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                          <li><strong>Child's Identity and Demographics:</strong> Child's name, age, date of birth, and gender.</li>
                          <li><strong>Family and Friends Information:</strong> First names of friends, siblings, and other relatives.</li>
                          <li><strong>Profile Details:</strong> Story theme, interests, and other information that helps personalize the child's story profile.</li>
                        </ul>
                        <p className="text-gray-700 leading-relaxed mt-2 italic">
                          <strong>Parental Consent:</strong> By submitting information about a child under the age of 13, you confirm that you are the child's parent or legal guardian and that you consent to the collection, processing, and use of the child's information as described herein.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">b. Information Collected Automatically</h3>
                    <p className="text-gray-700 leading-relaxed mb-2">
                      We also automatically collect certain information when you interact with our Service, including:
                    </p>
                    <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                      <li><strong>Device Information:</strong> IP address, browser type, operating system, and device identifiers.</li>
                      <li><strong>Usage Information:</strong> Pages visited, links clicked, time spent on pages, and other usage statistics.</li>
                      <li><strong>Diagnostic Data:</strong> Error logs and performance data that help us diagnose issues and improve our Service.</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">c. Cookies and Similar Technologies</h3>
                    <p className="text-gray-700 leading-relaxed mb-2">
                      We use cookies and similar tracking technologies to:
                    </p>
                    <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                      <li>Enhance your experience on our Site.</li>
                      <li>Remember your preferences.</li>
                      <li>Analyze website traffic and usage trends.</li>
                    </ul>
                    <p className="text-gray-700 leading-relaxed mt-2">
                      You can control the use of cookies through your browser settings, but please note that disabling cookies may affect your ability to use certain features of the Service.
                    </p>
                  </div>
                </div>
              </div>

              {/* Section 2 */}
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-gray-900">2. How We Use Your Information</h2>
                <p className="text-gray-700 leading-relaxed mb-2">
                  We use the information we collect for a variety of purposes, including to:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li><strong>Provide and Maintain the Service:</strong> Facilitate your access to the Service and ensure it functions properly.</li>
                  <li><strong>Manage Your Account:</strong> Process registrations, manage your account, and provide customer support.</li>
                  <li><strong>Create Story Profiles:</strong> Use the information provided for children to generate personalized story profiles that capture their identity, interests, and story themes.</li>
                  <li><strong>Improve the Service:</strong> Analyze usage data and feedback to make improvements to our Service.</li>
                  <li><strong>Communicate With You:</strong> Send updates, newsletters, and other communications that may be of interest to you.</li>
                  <li><strong>Ensure Security:</strong> Monitor and protect the Service against fraudulent or malicious activity.</li>
                </ul>
              </div>

              {/* Section 3 */}
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-gray-900">3. How We Share Your Information</h2>
                <p className="text-gray-700 leading-relaxed mb-2">
                  We may share your information in the following circumstances:
                </p>
                <div className="space-y-3 ml-4">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">a. With Service Providers</h3>
                    <p className="text-gray-700 leading-relaxed">
                      We engage trusted third-party companies and individuals to perform functions on our behalf (such as hosting, data analytics, payment processing, and email delivery). These service providers are required to maintain the confidentiality of your Personal Information and are prohibited from using your data for any other purpose.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">b. For Legal Reasons</h3>
                    <p className="text-gray-700 leading-relaxed">
                      We may disclose your information if required to do so by law or in response to valid requests by public authorities (e.g., a court or governmental agency).
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">c. Business Transfers</h3>
                    <p className="text-gray-700 leading-relaxed">
                      In the event of a merger, acquisition, or sale of all or a portion of our assets, your information may be transferred as part of that transaction. We will notify you via email and/or a prominent notice on our Site if your Personal Information is transferred to a new owner.
                    </p>
                  </div>
                </div>
              </div>

              {/* Section 4 */}
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-gray-900">4. Data Security</h2>
                <p className="text-gray-700 leading-relaxed">
                  We implement appropriate technical and organizational measures designed to secure your Personal Information from accidental or unlawful destruction, loss, alteration, unauthorized disclosure, or access. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
                </p>
              </div>

              {/* Section 5 */}
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-gray-900">5. Data Retention</h2>
                <p className="text-gray-700 leading-relaxed">
                  We retain your Personal Information only for as long as necessary to fulfill the purposes for which it was collected, comply with our legal obligations, resolve disputes, and enforce our agreements. When your data is no longer needed, we will take reasonable steps to delete or anonymize it.
                </p>
              </div>

              {/* Section 6 */}
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-gray-900">6. Your Rights and Choices</h2>
                <p className="text-gray-700 leading-relaxed mb-2">
                  Depending on your jurisdiction, you may have the following rights regarding your Personal Information:
                </p>
                <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                  <li><strong>Access:</strong> Request a copy of the information we hold about you.</li>
                  <li><strong>Correction:</strong> Request that we update or correct inaccurate or incomplete information.</li>
                  <li><strong>Deletion:</strong> Request that we delete your Personal Information.</li>
                  <li><strong>Restriction:</strong> Request that we limit our processing of your Personal Information.</li>
                  <li><strong>Portability:</strong> Request that we transfer your Personal Information to another party.</li>
                  <li><strong>Objection:</strong> Object to our processing of your Personal Information.</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-2">
                  If you wish to exercise any of these rights, please contact us using the details provided in the "Contact Us" section below. We may require additional information to verify your identity before processing your request.
                </p>
              </div>

              {/* Section 7 */}
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-gray-900">7. Children's Privacy</h2>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Collection and Use of Children's Information:</h3>
                    <p className="text-gray-700 leading-relaxed">
                      Our Service includes the option to create personalized story profiles for children under the age of 13. To create these profiles, we collect specific information about the child (as detailed in Section 1a under "For Children's Story Profiles"). This information is collected and processed solely for the purpose of generating the child's story profile and enhancing their experience on our Service.
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Parental Involvement and Consent:</h3>
                    <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                      <li><strong>Parental Consent:</strong> By providing information for a child under 13, you confirm that you are the child's parent or legal guardian and consent to the collection, processing, and use of the child's information as described in this Privacy Policy.</li>
                      <li><strong>Review and Update:</strong> If you need to review or update the information provided about your child, please contact us using the details in the "Contact Us" section. We are committed to ensuring that the information we hold is accurate and up-to-date.</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">No Direct Collection from Children:</h3>
                    <p className="text-gray-700 leading-relaxed">
                      We do not knowingly collect personal information directly from children under the age of 13 without the consent of a parent or legal guardian.
                    </p>
                  </div>
                </div>
              </div>

              {/* Section 8 */}
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-gray-900">8. International Data Transfers</h2>
                <p className="text-gray-700 leading-relaxed">
                  Your Personal Information may be transferred to, stored, and processed in countries other than your own. By using our Service, you consent to the transfer of your information to countries that may have different data protection rules than your country.
                </p>
              </div>

              {/* Section 9 */}
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-gray-900">9. Changes to This Privacy Policy</h2>
                <p className="text-gray-700 leading-relaxed">
                  We may update this Privacy Policy from time to time. Any changes will be posted on this page, along with the updated "Effective Date." We encourage you to review this Privacy Policy periodically to stay informed about how we are protecting your information.
                </p>
              </div>

              {/* Section 10 */}
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-gray-900">10. Contact Us</h2>
                <p className="text-gray-700 leading-relaxed">
                  If you have any questions, concerns, or requests regarding this Privacy Policy or our privacy practices, please contact us at:
                </p>
                <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                  <div className="flex items-center gap-3">
                    <Mail className="h-5 w-5 text-purple-600" />
                    <a href="mailto:support@mystoryme.com" className="text-purple-600 font-semibold hover:text-purple-700">
                      support@mystoryme.com
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Privacy;


